import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Icon from '@components/icon/Icons'
import SiteIcon from '@components/icon/SiteIcons'

import useI18n from '@i18n/useI18n'

import { getNextActionOnSiteNumber } from '@utils/userUtils'

interface Props {
  onSiteClicked: () => void
  site: SiteResponse
  stats?: IncidentsStatistiquesResponse
  displayChecklistActions?: boolean
}

const SiteCard = ({ onSiteClicked, site, stats, displayChecklistActions = true }: Props) => {
  const i18n = useI18n()

  const nextActionsOnSite = getNextActionOnSiteNumber(site)

  const displayedStat = (key: keyof IncidentsStatistiquesResponse): number | string =>
    !!stats && stats[key] !== null && stats[key] !== undefined ? stats[key]! : i18n.t('errors.NC')

  return (
    <Container key={site.id} activeOpacity={0.9} onPress={onSiteClicked}>
      <NameContainer>
        <Name>{i18n.t(`screens.site.sites.${site.codeLieu}`)}</Name>
        <SiteIcon name={site.codeLieu as SiteIconName} width={110} color={theme.colors.cardIcon} />
      </NameContainer>
      {displayChecklistActions && !!nextActionsOnSite && (
        <ImportantIncident>
          <Icon name="problem" size={20} color={theme.colors.alertButton} />
          <ImportantIncidentText>{i18n.t('screens.site.actions', { count: nextActionsOnSite })}</ImportantIncidentText>
        </ImportantIncident>
      )}
      {!!stats && (
        <IndicatorsContainer>
          <IndicatorContainer>
            <IndicatorTitle>{i18n.t('screens.home.newIncidents')}</IndicatorTitle>
            <IndicatorCard>
              <Indicator>
                <Value>{displayedStat('creePrestation')}</Value>
                <Legend>{i18n.t('screens.home.prestation')}</Legend>
              </Indicator>
              <SeparatorY />
              <Indicator>
                <Value>{displayedStat('creeInfrastructure')}</Value>
                <Legend>{i18n.t('screens.home.infrastructure')}</Legend>
              </Indicator>
            </IndicatorCard>
          </IndicatorContainer>
          <IndicatorContainer isRight>
            <IndicatorTitle>{i18n.t('screens.home.total')}</IndicatorTitle>
            <IndicatorCard>
              <Indicator>
                <Value>{displayedStat('enCours')}</Value>
                <Legend>{i18n.t('screens.home.inProgress')}</Legend>
              </Indicator>
            </IndicatorCard>
          </IndicatorContainer>
        </IndicatorsContainer>
      )}
    </Container>
  )
}

export default SiteCard

const Container = styled(TouchableOpacity)`
  border-radius: 8px;
  padding: 16px;
  ${(props) => props.theme.breakpoint !== 'phone' && `width: 290px;`}
  justify-content: space-between;
  background-color: ${(props) => props.theme.colors.background};
  ${(props) => props.theme.constants.shadow};
`
const NameContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  margin-bottom: 10px;
`
const Name = styled(Text)`
  flex: 1;
  ${(props) => props.theme.fonts.Headline};
`
const ImportantIncident = styled(View)`
  flex-direction: row;
  align-items: center;
  border-radius: 8px;
  padding: 5px 14px;
  min-height: 40px;
  background-color: ${(props) => props.theme.colors.alertButtonLight};
`
const ImportantIncidentText = styled(Text)`
  ${(props) => props.theme.fonts.Caption2};
  font-size: 14px;
  color: ${(props) => props.theme.colors.alertDark};
  margin-left: 10px;
`
const SeparatorY = styled(View)`
  height: 34px;
  width: 1px;
  margin: 8px 0;
  background-color: ${(props) => props.theme.colors.disable};
`
const IndicatorsContainer = styled(View)`
  flex-direction: row;
  margin-top: 10px;
`
const IndicatorTitle = styled(Text)`
  ${(props) => props.theme.fonts.Caption1};
  color: ${(props) => props.theme.colors.secondaryText};
  margin-bottom: 8px;
  font-size: 14px;
`
const IndicatorContainer = styled(View)<{ isRight?: boolean }>`
  ${(props) =>
    props.isRight
      ? `  margin-left: 8px;
           flex:1;
        `
      : `flex: 2;`}
`
const IndicatorCard = styled(View)`
  height: 50px;
  background-color: ${(props) => props.theme.colors.cardSecondary};
  flex-direction: row;
  align-items: center;
`
const Indicator = styled(View)`
  flex: 1;
`

const Value = styled(Text)`
  ${(props) => props.theme.fonts.Subheadline};
  color: ${(props) => props.theme.colors.primaryText};
  text-align: center;
`

const Legend = styled(Text)`
  ${(props) => props.theme.fonts.Caption2};
  color: ${(props) => props.theme.colors.secondaryText};
  font-size: 13px;
  text-align: center;
`
